import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [ssoExchanging, setSsoExchanging] = useState(false);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Single Sign-On from Luxsun Hub: detect ?luxsun_token=... once on mount,
  // exchange it for a real session, strip it from the URL, then continue.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('luxsun_token');
    if (!ssoToken) return;

    setSsoExchanging(true);

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/sso`, { token: ssoToken });
        const { access_token, user: userData } = res.data;
        localStorage.setItem('auth_token', access_token);
        setToken(access_token);
        setUser(userData);
      } catch (err) {
        console.error('SSO exchange failed:', err);
        const detail = err?.response?.data?.detail || 'SSO login mislykkedes';
        // Surface a non-fatal message; user can still use normal login
        if (typeof window !== 'undefined') {
          window.__sso_error = detail;
        }
      } finally {
        // Always strip the token + tracking params from the URL so it doesn't
        // hang around in browser history or get sent in Referer headers.
        params.delete('luxsun_token');
        params.delete('u');
        params.delete('source');
        const newSearch = params.toString();
        const newUrl =
          window.location.pathname +
          (newSearch ? `?${newSearch}` : '') +
          window.location.hash;
        window.history.replaceState({}, document.title, newUrl);
        setSsoExchanging(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user on mount
  useEffect(() => {
    // Skip auto-load during SSO exchange; the exchange itself will set user.
    if (ssoExchanging) {
      return;
    }
    const loadUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Failed to load user:', error);
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [token, ssoExchanging]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('auth_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
    }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
